import { useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import {
  FaArrowRight,
  FaArrowLeft,
  FaLocationDot,
  FaRegClock,
  FaRegCalendar,
  FaBriefcase,
  FaGraduationCap,
} from "react-icons/fa6";
import Header from "../components/header";
import Footer from "../components/footer";
import ApplicationModal from "../components/application-modal";
import { useCareersContent } from "../hooks/careers-content";
import { WEB_ROUTES } from "../routes/web-routes";

export default function CareersJobDetails() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { jobRef } = useParams();
  const { content, isLoading } = useCareersContent();

  const normalizedJobRef = (jobRef ?? "").trim().toLowerCase();
  const parsedJobId = Number.parseInt(normalizedJobRef, 10);
  const isValidId = Number.isInteger(parsedJobId) && parsedJobId > 0;
  const jobs = content?.jobs ?? [];

  // Match on slug first, then post ID, then fall back to a 1-based index so
  // ACF-defined jobs (which have no post ID) stay reachable by position.
  const selectedJob =
    jobs.find(
      (job) => (job.slug ?? "").trim().toLowerCase() === normalizedJobRef,
    ) ??
    jobs.find((job) => job.id === parsedJobId) ??
    (isValidId ? jobs[parsedJobId - 1] : undefined);

  if (isLoading) {
    return (
      <>
        <Header />
        <main>
          <section className="bg-white md:py-16 py-8">
            <div className="container mx-auto md:px-0 px-8">
              <p className="text-lg text-gray-600">Loading job details…</p>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  if (!selectedJob) {
    return (
      <>
        <Helmet>
          <title>{`Job not found - Nant Power`}</title>
        </Helmet>
        <Header />
        <main>
          <section className="bg-white md:py-16 py-8">
            <div className="container mx-auto md:px-0 px-8">
              <h1 className="mb-2 leading-7 text-[#0070b9] md:text-4xl text-3xl font-semibold">
                Job not found
              </h1>

              <p className="text-lg text-gray-700 mb-8">
                The job you are looking for does not exist or is no longer
                available.
              </p>
              <NavLink
                to={WEB_ROUTES.CAREERS}
                className="bg-[#58b947] px-5 py-2 text-white inline-flex items-center gap-2"
              >
                <FaArrowLeft className="w-4 h-4" />
                Back to careers
              </NavLink>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${selectedJob.title} - Careers - Nant Power`}</title>
        <meta
          name="description"
          content={`${selectedJob.title}${
            selectedJob.location ? ` — ${selectedJob.location}` : ""
          }. Apply now to join the team at Nant Power.`}
        />
      </Helmet>

      <Header />
      <main>
        <section className="bg-gray-100 md:py-16 py-8">
          <div className="container mx-auto md:px-0 px-8">
            <NavLink
              to={WEB_ROUTES.CAREERS}
              className="inline-flex items-center gap-2 text-[#58b947] hover:text-[#0070b9] mb-8 transition-colors"
            >
              <FaArrowLeft className="w-4 h-4" />
              Back to careers
            </NavLink>

            <h1 className="mb-2 leading-7 text-[#0070b9] md:text-4xl text-3xl font-semibold">
              {selectedJob.title}
            </h1>

            <div className="flex flex-wrap gap-4 text-base text-gray-600">
              {selectedJob.grade && (
                <div className="flex items-center gap-2">
                  <FaGraduationCap className="w-5 h-5 text-[#58b947]" />
                  <span>Grade: {selectedJob.grade}</span>
                </div>
              )}
              {selectedJob.department && (
                <div className="flex items-center gap-2">
                  <FaBriefcase className="w-4 h-4 text-[#58b947]" />
                  <span>{selectedJob.department}</span>
                </div>
              )}
              {selectedJob.location && (
                <div className="flex items-center gap-2">
                  <FaLocationDot className="w-4 h-4 text-[#58b947]" />
                  <span>{selectedJob.location}</span>
                </div>
              )}
              {selectedJob.type && (
                <div className="flex items-center gap-2">
                  <FaRegClock className="w-4 h-4 text-[#58b947]" />
                  <span>{selectedJob.type}</span>
                </div>
              )}
              {selectedJob.closingDate && (
                <div className="flex items-center gap-2">
                  <FaRegCalendar className="w-4 h-4 text-[#58b947]" />
                  <span>Closing: {selectedJob.closingDate}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="bg-white md:py-16 py-8">
          <div className="container mx-auto md:px-0 px-8">
            <div className="mb-10">
              <h2 className="text-3xl font-medium mb-4 text-[#58b947]">
                Job details
              </h2>
              <p className="text-lg leading-relaxed">
                {selectedJob.description}
              </p>
            </div>

            {selectedJob.details && (
              <div
                className="text-lg text-gray-700 leading-relaxed [&_p]:mb-4 [&_h2]:text-3xl [&_h2]:font-medium [&_h2]:mb-4 [&_h2]:mt-8 [&_h2]:text-[#58b947] [&_h3]:text-2xl [&_h3]:font-medium [&_h3]:mb-3 [&_h3]:mt-6 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1 [&_a]:text-[#58b947] [&_a]:underline [&_strong]:font-semibold [&_img]:my-6 [&_img]:w-full"
                dangerouslySetInnerHTML={{ __html: selectedJob.details }}
              />
            )}

            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="bg-[#58b947] px-5 py-2 text-white inline-flex items-center gap-2 mt-10"
            >
              Apply now
              <FaArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      </main>
      <Footer />

      <GoogleReCaptchaProvider reCaptchaKey="6LfcxHorAAAAAAdbYDy1YHUNOxmQeE9lrjMZa0tX">
        <ApplicationModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          jobId={selectedJob.id}
          jobSlug={selectedJob.slug}
          jobTitle={selectedJob.title}
        />
      </GoogleReCaptchaProvider>
    </>
  );
}
